import dayjs from '../../../utils/date'

const { __FS_API_URL__ } = global
const apiPath: string = __FS_API_URL__ || 'https://www.money.co.uk/api'

export const fetchProfile = async (): Promise<ProfileFormData> => {
  const res = await fetch(`${apiPath}/sme-profile-session`)
  const data = await res.json()
  if (res.ok) {
    return profileDataToFormData(data.profile)
  } else {
    throw new Error(data.error)
  }
}

export const prefillFormData = async (
  formData: ProfileFormData,
  setValue: (name: string, value: any) => void,
  register: (name: string) => void,
  track: (detail: string, value: any) => void,
) => {
  setValue('businessNameInput', formData.businessDetails.businessName ?? '')
  if (formData.businessDetails) {
    for (const [key, value] of Object.entries(formData.businessDetails)) {
      register(`businessDetails.${key}`)
      setValue(`businessDetails.${key}`, value)
      track(key, value)
    }
  }
  if (formData.directorDetails) {
    for (const [key, value] of Object.entries(formData.directorDetails)) {
      register(`directorDetails.${key}`)
      setValue(`directorDetails.${key}`, value)
      track(key, value)
    }
  }
  if (formData.businessDetails.businessType === 'limitedCompany') {
    const res = await fetch(`${apiPath}/company/${formData.businessDetails.companyNumber}/details`)
    if (res.ok) {
      const companyData = await res.json()
      register(`businessDetails.sic_code`)
      setValue(`businessDetails.sic_code`, companyData.data.sic_code)
      register(`businessDetails.activePSCs`)
      setValue(`businessDetails.activePSCs`, companyData.data.activePSCs)
    }
  }
}

interface Profile {
  business?: Business
  personal: Personal
  customer_id?: string
  request_id?: string
  [property: string]: any
}

interface Personal {
  email: string
  title?: string
  first_name?: string
  last_name?: string
  dob?: string
  mobile_number?: string
  nationality?: string
  address?: Address
}
interface Business {
  address?: Address
  annual_turnover?: number
  borrowing_needs?: any
  business_type?: string
  company_name?: string
  company_number?: string
  date_of_creation?: string
  director?: Director
  employees?: number
  existence_years?: string
  industry?: string
  phone_number?: string
  physical_premises?: boolean
  startup?: boolean
  activePSCs?: any
  [property: string]: any
}

interface Address {
  address_line_1?: string
  address_line_2?: string
  city?: string
  country?: string
  county?: string
  number?: string
  organisation?: string
  postal_code?: string
  postcode?: string
  posttown?: string
  premise?: string
  street?: string
  summaryline?: string
  udprn?: string
  uprn?: string
  [property: string]: any
}

interface Director {
  dob?: string
  email?: string
  first_name?: string
  last_name?: string
  mobile_number?: string
  nationality?: string
  title?: string
  [property: string]: any
}

interface FormattedDateOfBirth {
  year: number
  month: number
  day: number
}

interface BusinessDetails {
  businessName?: string
  companyNumber?: string
  businessType?: string
  activePSCs?: any
  numberOfEmployees?: number
  turnover?: number
  industryName?: string
  date_of_creation?: string
  dateOfCreation?: string
  address?: Address
  borrowingAmount?: any
}

interface DirectorDetails {
  title?: string
  firstName?: string
  lastName?: string
  dateOfBirthFields?: FormattedDateOfBirth
  emailAddress?: string
  mobileNumber?: string
  address?: Address
  nationality?: string
}

export interface ProfileFormData {
  businessDetails: BusinessDetails
  directorDetails: DirectorDetails
}

const profileDataToFormData = (profile: Profile): ProfileFormData => {
  const { business, personal } = profile
  return {
    businessDetails: {
      ...(business?.company_name ? { businessName: business.company_name } : {}),
      ...(business?.company_number ? { companyNumber: business.company_number } : {}),
      ...(business?.business_type ? { businessType: business.business_type } : {}),
      ...(business?.activePSCs ? { activePSCs: business.activePSCs } : {}),
      ...(business?.employees ? { numberOfEmployees: business.employees } : {}),
      ...(business?.annual_turnover ? { turnover: business.annual_turnover } : {}),
      ...(business?.industry ? { industryName: business.industry } : {}),
      ...(business?.date_of_creation ? { date_of_creation: business.date_of_creation } : {}),
      ...(business?.address ? { address: business.address } : {}),
      ...(business?.borrowing_needs?.amount
        ? { borrowingAmount: business.borrowing_needs.amount }
        : {}),
      ...(business?.existence_years ? { existenceYears: business.existence_years } : {}),
      ...(business?.company_category ? { companyType: business.company_category } : {}),
    },
    directorDetails: {
      ...(personal.title ? { title: personal.title } : {}),
      ...(business?.director?.first_name ? { firstName: business?.director?.first_name } : {}),
      ...(business?.director?.last_name ? { lastName: business?.director?.last_name } : {}),
      ...(profile.business?.director?.dob ?? profile.personal.dob
        ? {
            dateOfBirthFields: {
              year: dayjs(profile.business?.director?.dob ?? profile.personal.dob).get('year'),
              month:
                dayjs(profile.business?.director?.dob ?? profile.personal.dob).get('month') + 1,
              day: dayjs(profile.business?.director?.dob ?? profile.personal.dob).get('date'),
            },
          }
        : {}),
      ...(business?.director?.email ?? profile.personal?.email
        ? { emailAddress: business?.director?.email ?? profile.personal?.email }
        : {}),
      ...(business?.director?.mobile_number
        ? { mobileNumber: business?.director?.mobile_number }
        : {}),
      ...(personal?.address ? { address: personal.address } : {}),
      ...(business?.director?.nationality ? { nationality: business.director.nationality } : {}),
    },
  }
}

export const piiData = [
  'emailAddress',
  'homeAddress',
  'address',
  'firstName',
  'lastName',
  'dateOfBirthFields.day',
  'dateOfBirthFields.month',
  'dateOfBirthFields.year',
  'title',
  'mobileNumber',
  'nationality',
  'address_line_1',
  'address_line_2',
  'postal_code',
  'locality',
  'country',
]
